/**
 * The languages a telecaller picks from, and how a stored value is displayed.
 *
 * Free text on the way in was a mistake: the dev book already holds both
 * "Hindi" (written by the customer ingest, straight off the lender's file) and
 * "hi" (typed into the console), which are the same fact stored two ways and
 * rendered as two different badges. A caller scanning a queue cannot tell
 * whether "HI" and "HINDI" mean the same customer need.
 *
 * So: the dropdown writes the full English name, matching what lender files
 * actually carry, and [languageLabel] folds the ISO codes already in the data
 * onto the same label on the way out. Anything unrecognised is shown as
 * stored — a lender may well send a language this list does not know, and
 * hiding it would be worse than showing it verbatim.
 */
export const LANGUAGES = [
  'English', 'Hindi', 'Bengali', 'Marathi', 'Telugu', 'Tamil', 'Gujarati',
  'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Assamese', 'Urdu',
] as const

/** ISO 639-1 codes seen in stored data, folded onto the names above. */
const CODE_TO_NAME: Record<string, string> = {
  en: 'English', hi: 'Hindi', bn: 'Bengali', mr: 'Marathi', te: 'Telugu',
  ta: 'Tamil', gu: 'Gujarati', kn: 'Kannada', ml: 'Malayalam', pa: 'Punjabi',
  or: 'Odia', as: 'Assamese', ur: 'Urdu',
}

/** Display form of a stored language: "hi" → "Hindi", "Bhojpuri" → "Bhojpuri". */
export function languageLabel(raw: string | null | undefined): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!trimmed) return null
  return CODE_TO_NAME[trimmed.toLowerCase()] ?? trimmed
}

/**
 * English is the assumed default, so it earns no badge — the badge exists to
 * flag the call that needs a different agent. Both "en" and "English" are in
 * the data, hence the fold rather than a bare comparison.
 */
export function isDefaultLanguage(raw: string | null | undefined): boolean {
  return languageLabel(raw) === 'English'
}
